export const IOS_APP_STORE_ID = '6776295842';
export const ANDROID_PACKAGE_ID = 'co.mycandle.mobile';

export const APP_STORE_URL = `https://apps.apple.com/app/id${IOS_APP_STORE_ID}`;
export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_ID}`;

export function isAndroidDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
}

export function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return true;
  // iPadOS 13+ reports MacIntel with touch points
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

export function isMobileDevice(): boolean {
  return isAndroidDevice() || isIOSDevice();
}

export function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  const isStandaloneDisplay = window.matchMedia?.('(display-mode: standalone)')?.matches ?? false;
  const isIOSStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return isStandaloneDisplay || isIOSStandalone;
}

export function getStoreUrlForDevice(): string {
  return isAndroidDevice() ? PLAY_STORE_URL : APP_STORE_URL;
}

/**
 * Maps a web pathname + search string to a `candle://` custom-scheme deep link
 * when the current route has a direct mobile app counterpart (matching
 * `.well-known/apple-app-site-association` and `mobile/app.json` intentFilters).
 * Returns `null` for web-only or general marketing routes.
 */
export function getDeepLinkForPath(pathname: string, search = ''): string | null {
  const clean = pathname.replace(/\/+$/, '') || '/';

  // Short QR / share links: /m/:shortCode
  if (/^\/m\/[^/]+$/i.test(clean)) {
    return `candle://${clean.slice(1)}${search}`;
  }

  // Memorial pages: /memorial/:slug (exclude web-only sub-routes & unavailable/create)
  if (/^\/memorial\/[^/]+$/i.test(clean)) {
    const seg = clean.split('/')[2]?.toLowerCase();
    if (seg && seg !== 'unavailable' && seg !== 'create') {
      return `candle://${clean.slice(1)}${search}`;
    }
  }

  // Community vigils: /vigil/:slug
  if (/^\/vigil\/[^/]+$/i.test(clean)) {
    return `candle://${clean.slice(1)}${search}`;
  }

  // Invite links: /join/:code
  if (/^\/join\/[^/]+$/i.test(clean)) {
    return `candle://${clean.slice(1)}${search}`;
  }

  // Public A-Tag hubs: /atag/:slug (exclude manage, create, search)
  if (/^\/atag\/[^/]+$/i.test(clean)) {
    const seg = clean.split('/')[2]?.toLowerCase();
    if (seg && seg !== 'manage' && seg !== 'create' && seg !== 'search') {
      return `candle://${clean.slice(1)}${search}`;
    }
  }

  return null;
}

/**
 * Attempts to open the native Candle app via custom scheme on mobile devices,
 * falling back to the appropriate app store (App Store / Google Play) if the app
 * does not launch within 1.5s. When no deepLink is given, opens the store directly.
 */
export function openInAppOrStore(deepLink?: string | null): void {
  if (typeof window === 'undefined') return;
  const storeUrl = getStoreUrlForDevice();

  if (!deepLink || !isMobileDevice()) {
    window.open(storeUrl, '_blank', 'noopener,noreferrer');
    return;
  }

  let appOpened = false;
  const onVisibilityChange = () => {
    if (document.hidden) appOpened = true;
  };
  const onPageHide = () => {
    appOpened = true;
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);

  window.location.href = deepLink;

  window.setTimeout(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
    if (!appOpened && !document.hidden) {
      window.location.href = storeUrl;
    }
  }, 1500);
}
